/**
 * Logger "freshness" derived from its last reading time. The ingestion
 * engine copies readings into the DB continuously, so the gap between the
 * newest reading and "now" tells the user whether the logger is reporting.
 * Rather than a flat "online", we surface staleness as a traffic light so a
 * stalled logger is obvious.
 *
 * Bands (age of the newest reading):
 *   Current  0 to 10 minutes (35 for a weather station)
 *   Delayed  over that, up to 30 minutes (60 for a weather station)
 *   Offline  over 30 minutes (60 for a weather station), or no reading at all
 *
 * A weather station ('07') reads every 15 minutes and the worker polls
 * ZENTRA every 15 minutes, so just before a poll a working station's newest
 * reading is up to 30 minutes old (one reading interval plus one poll
 * interval) plus upload lag: it stays Current for 35 minutes, and is Offline
 * only after 60 minutes, as the worker's own station check (backend
 * worker/jobs/logger_health.py WEATHER_STALE).
 *
 * With no reading yet the same bands measure the time since the channel's
 * start, labelled "Awaiting first reading", so a pour created with start =
 * now is not a red "Offline" before its first reading is due. No reading
 * and no known start is Offline.
 *
 * This is the one staleness rule: the control panel's "Stale data" flag is
 * "any logging channel Offline" by the same bands (backend
 * services/dashboard_service.py OFFLINE_AFTER and WEATHER_OFFLINE_AFTER).
 *
 * Two states sit outside the bands, so a channel is never shown as a red
 * "Offline" when nothing is wrong:
 *   Logging ended  the pour is completed, or the location's logging finished
 *   Scheduled      no reading yet and the planned start is still ahead
 */
import { parseUtc, type IsoLike } from './datetime';

export type OnlineLevel = 'online' | 'delayed' | 'offline' | 'ended' | 'scheduled';

export interface OnlineStatus {
  level: OnlineLevel;
  /** Hex colour aligned with MUI success / warning / error main (grey for
   *  ended, blue for scheduled). */
  color: string;
  /** Short human label, e.g. "Current", "Delayed", "Offline". */
  label: string;
  /** Started but no reading yet (and not yet Offline): the band is the
   *  time since the start, labelled "Awaiting first reading". */
  awaiting?: boolean;
}

const MINUTE_MS = 60 * 1000;
/** Newest reading is at most this old: the logger is reporting normally. */
export const CURRENT_MAX_MS = 10 * MINUTE_MS;
/** CURRENT_MAX_MS for a weather station ('07'): it reads every 15 minutes
 *  and the worker polls ZENTRA every 15 minutes, so a working station's
 *  newest reading reaches 30 minutes old (plus upload lag) before the next
 *  poll. It stays Current through that. */
export const WEATHER_CURRENT_MAX_MS = 35 * MINUTE_MS;
/** Newest reading is at most this old (but older than CURRENT_MAX_MS): delayed. */
export const DELAYED_MAX_MS = 30 * MINUTE_MS;
/** DELAYED_MAX_MS for a weather station ('07'), which uploads in bursts:
 *  Offline only after 60 minutes, as the worker's station check (backend
 *  worker/jobs/logger_health.py WEATHER_STALE) and the backend's
 *  WEATHER_OFFLINE_AFTER. */
export const WEATHER_DELAYED_MAX_MS = 60 * MINUTE_MS;

/** LoggerInfo.Type '07': a weather station, compared as the backend does. */
export const isWeatherStationType = (loggerType?: string | null): boolean =>
  (loggerType ?? '').trim() === '07';

const ONLINE: OnlineStatus = { level: 'online', color: '#2e7d32', label: 'Current' };
const DELAYED: OnlineStatus = { level: 'delayed', color: '#ed6c02', label: 'Delayed' };
const OFFLINE: OnlineStatus = { level: 'offline', color: '#d32f2f', label: 'Offline' };
const ENDED: OnlineStatus = { level: 'ended', color: '#757575', label: 'Logging ended' };
const SCHEDULED: OnlineStatus = { level: 'scheduled', color: '#185FA5', label: 'Scheduled' };
/** Started, no reading yet: the bands from the start, under their own label. */
const AWAITING: OnlineStatus = { ...ONLINE, label: 'Awaiting first reading', awaiting: true };
const AWAITING_DELAYED: OnlineStatus = { ...DELAYED, label: 'Awaiting first reading', awaiting: true };

export interface OnlineContext {
  /** The channel's logging has ended (pour completed, or its own end has
   *  passed): "Logging ended" instead of a freshness band. */
  ended?: boolean;
  /** The planned start. Still ahead with no reading yet: "Scheduled"
   *  instead of "Offline". */
  startsAt?: IsoLike;
  /** A weather station: Current up to WEATHER_CURRENT_MAX_MS (35 minutes)
   *  instead of CURRENT_MAX_MS (10), and Delayed up to WEATHER_DELAYED_MAX_MS
   *  (60) instead of DELAYED_MAX_MS (30) before Offline. */
  weatherStation?: boolean;
}

/** Classify a logger's freshness from its last-read timestamp. A missing or
 *  unparseable timestamp is Scheduled while `context.startsAt` is ahead,
 *  "Awaiting first reading" (Current, then Delayed) for 30 minutes after it
 *  (60 for a weather station), and Offline after that or when the start is
 *  unknown. A channel whose logging ended is "Logging ended" whatever its
 *  last reading.
 *
 *  Backend datetimes are naive UTC strings (no `Z`), so they must go through
 *  `parseUtc`; a bare `new Date()` would read them as browser-local time and
 *  skew the age by the user's UTC offset.
 *
 *  `now` is injectable for tests; it defaults to the wall clock. */
export const getOnlineStatus = (
  lastRead?: string | number | Date | null,
  now: number = Date.now(),
  context: OnlineContext = {},
): OnlineStatus => {
  if (context.ended) return ENDED;
  const currentMax = context.weatherStation ? WEATHER_CURRENT_MAX_MS : CURRENT_MAX_MS;
  const delayedMax = context.weatherStation ? WEATHER_DELAYED_MAX_MS : DELAYED_MAX_MS;
  const ts = parseUtc(lastRead).getTime();
  if (Number.isNaN(ts)) {
    const start = parseUtc(context.startsAt).getTime();
    if (Number.isNaN(start)) return OFFLINE;
    if (start > now) return SCHEDULED;
    const sinceStart = now - start;
    if (sinceStart <= currentMax) return AWAITING;
    if (sinceStart <= delayedMax) return AWAITING_DELAYED;
    return OFFLINE;
  }
  const age = now - ts;
  if (age <= currentMax) return ONLINE;
  if (age <= delayedMax) return DELAYED;
  return OFFLINE;
};

/** Whether a pour status means monitoring is over: the code '2' (dashboard
 *  summary, chart response) or "Complete" / "Completed" (pour list). */
export const pourStatusEnded = (status?: string | null): boolean => {
  const s = (status ?? '').trim().toLowerCase();
  return s === '2' || s === 'complete' || s === 'completed';
};

/** The timing fields of a dashboard location the freshness rule reads.
 *  Structural, so older responses without the newer fields still fit. */
export interface LocationTiming {
  last_read?: IsoLike;
  /** PourLoggerStatus.LoggingStarted. */
  logging_started?: IsoLike;
  /** PourLocation.LogginStart: the planned start from pour setup. */
  scheduled_start?: IsoLike;
  /** When the location's logging finishes or finished. */
  logging_ended?: IsoLike;
  /** LoggerInfo.Type: a weather station ('07') is Offline after 60 minutes,
   *  not 30. */
  logger_type?: string | null;
}

/** The location's logging has ended: its pour is completed, or its own
 *  logging end has passed. */
export const isLoggingEnded = (
  location: LocationTiming | null | undefined,
  pourStatus?: string | null,
  now: number = Date.now(),
): boolean => {
  if (pourStatusEnded(pourStatus)) return true;
  const end = parseUtc(location?.logging_ended).getTime();
  return !Number.isNaN(end) && end <= now;
};

/** When the location starts logging: its planned start, else the logger's. */
export const locationStartsAt = (location: LocationTiming | null | undefined): IsoLike =>
  location?.scheduled_start ?? location?.logging_started;

/** Freshness of a dashboard location within its pour: the bands (a weather
 *  station's 60 minutes from its logger_type), or "Logging ended" /
 *  "Scheduled" (see getOnlineStatus). */
export const locationOnlineStatus = (
  location: LocationTiming,
  pourStatus?: string | null,
  now: number = Date.now(),
): OnlineStatus =>
  getOnlineStatus(location.last_read, now, {
    ended: isLoggingEnded(location, pourStatus, now),
    startsAt: locationStartsAt(location),
    weatherStation: isWeatherStationType(location.logger_type),
  });
